import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Plus, Trash2, Edit3, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ListsPanel — manage reusable Contact Lists (General Newsletter,
 * Orlando Contacts, Sponsors, etc.). Tags are free-form on contacts —
 * no CRUD needed for them.
 */
export const ListsPanel = ({ lists, onReload, canDelete }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#002249');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const resetAdd = () => { setName(''); setColor('#002249'); setDescription(''); setAdding(false); };

  const create = async () => {
    if (name.trim().length < 2) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/contact-lists`, { name, color, description }, { withCredentials: true });
      toast.success('List created');
      resetAdd();
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/contact-lists/${editing.id}`, {
        name: editing.name, color: editing.color, description: editing.description,
      }, { withCredentials: true });
      toast.success('List updated');
      setEditing(null);
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteList = async (l) => {
    if (!window.confirm(`Delete list "${l.name}"? Contacts remain, but they will no longer be in this list.`)) return;
    try {
      await axios.delete(`${API}/admin/contact-lists/${l.id}`, { withCredentials: true });
      toast.success('List removed');
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="lists-panel">
      <div className="flex items-center gap-2 mb-4">
        <Button onClick={() => setAdding(true)} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="list-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New list
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(lists || []).length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-10 border border-dashed border-slate-200 rounded-xl">
            No lists yet. Create your first segment like "General Newsletter".
          </div>
        )}
        {(lists || []).map((l) => (
          <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`list-tile-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-md flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: l.color || '#002249' }}>
                  <Tag className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-[13.5px] text-[#1a1a1a] truncate">{l.name}</div>
                  <div className="text-[10.5px] text-slate-500">{(l.count ?? 0).toLocaleString()} contact(s)</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => setEditing(l)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`list-edit-${l.id}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                {canDelete && (
                  <button onClick={() => deleteList(l)} className="p-1 rounded hover:bg-rose-50 text-rose-500" title="Delete" data-testid={`list-delete-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            {l.description && (
              <div className="text-[11.5px] text-slate-600 mt-2 line-clamp-2">{l.description}</div>
            )}
          </div>
        ))}
      </div>

      {/* Add */}
      {adding && (
        <ListModal
          title="New list"
          onClose={resetAdd}
          name={name} setName={setName}
          color={color} setColor={setColor}
          description={description} setDescription={setDescription}
          onSave={create}
          busy={busy}
        />
      )}

      {/* Edit */}
      {editing && (
        <ListModal
          title={`Edit "${editing.name}"`}
          onClose={() => setEditing(null)}
          name={editing.name} setName={(v) => setEditing(e => ({...e, name: v}))}
          color={editing.color || '#002249'} setColor={(v) => setEditing(e => ({...e, color: v}))}
          description={editing.description || ''} setDescription={(v) => setEditing(e => ({...e, description: v}))}
          onSave={saveEdit}
          busy={busy}
        />
      )}
    </div>
  );
};

const ListModal = ({ title, onClose, name, setName, color, setColor, description, setDescription, onSave, busy }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="list-modal">
    <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
        <div className="font-semibold text-[13px] text-[#1a1a1a]">{title}</div>
        <button onClick={onClose} className="p-1 rounded-md text-slate-500 hover:bg-slate-50"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Name</label>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="e.g. Orlando Contacts" data-testid="list-name-input" />
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Description</label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" placeholder="Optional" />
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Color</label>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="mt-1 w-16 h-9 border border-slate-200 rounded-md" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} disabled={busy} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="list-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default ListsPanel;
